import {
    collection,
    getDocs,
    query,
    where,
    writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
    calculateRegistrantScoreAndAward,
    isAwardSyncNeeded,
} from './scoringCalculator';
import { fetchVideoPenaltyConfigs } from './videoPenaltyConfig';

const SCORE_QUERY_CHUNK_SIZE = 10;
const WRITE_BATCH_LIMIT = 499;

export const splitFirestoreInValues = (values, size = SCORE_QUERY_CHUNK_SIZE) => {
    const chunks = [];
    for (let index = 0; index < values.length; index += size) {
        chunks.push(values.slice(index, index + size));
    }
    return chunks;
};

export const groupScoresByRegistrant = (scoreDocuments) => (
    (scoreDocuments || []).reduce((groupedScores, scoreDocument) => {
        const score = typeof scoreDocument.data === 'function'
            ? { id: scoreDocument.id, ...scoreDocument.data() }
            : scoreDocument;
        if (!score?.registrantId) return groupedScores;
        if (!groupedScores[score.registrantId]) groupedScores[score.registrantId] = [];
        groupedScores[score.registrantId].push(score);
        return groupedScores;
    }, {})
);

export const calculateRegistrantScoringMap = (
    registrants,
    scoresByRegistrant,
    videoPenaltyConfigsByEvent
) => Object.fromEntries((registrants || []).map((registrant) => [
    registrant.id,
    calculateRegistrantScoreAndAward(
        scoresByRegistrant[registrant.id] || [],
        registrant,
        videoPenaltyConfigsByEvent[registrant.eventId] || null
    ),
]));

export const createScoringExportFields = (scoring) => ({
    'Video Limit': scoring?.videoPenaltyMaximumMinutes !== null
        && scoring?.videoPenaltyMaximumMinutes !== undefined
        ? `${scoring.videoPenaltyMaximumMinutes} minutes`
        : 'Not configured',
    'Video Penalty Config Status': scoring?.videoPenaltyRuleStatus || 'not-scored',
    'Auto Penalty applied': scoring?.autoPenaltyScore > 0 ? `-${scoring.autoPenaltyScore}` : 'None',
    'Manual Penalty applied': scoring?.manualPenaltyScore > 0 ? `-${scoring.manualPenaltyScore}` : 'None',
    'Average Score': scoring?.hasScores ? scoring.finalAverageScore : 0,
    'Final Award': scoring?.hasScores ? scoring.finalAward : '-',
});

export const fetchRegistrantScoringResults = async (registrants) => {
    const registrantIds = [...new Set((registrants || []).map(({ id }) => id).filter(Boolean))];
    const videoPenaltyConfigsByEvent = await fetchVideoPenaltyConfigs(
        (registrants || []).map(({ eventId }) => eventId)
    );

    const scoreSnapshots = await Promise.all(
        splitFirestoreInValues(registrantIds).map((registrantIdChunk) => getDocs(query(
            collection(db, 'JuryScores2025'),
            where('registrantId', 'in', registrantIdChunk)
        )))
    );
    const scoresByRegistrant = groupScoresByRegistrant(
        scoreSnapshots.flatMap((snapshot) => snapshot.docs)
    );

    return {
        scoresByRegistrant,
        scoringByRegistrant: calculateRegistrantScoringMap(
            registrants,
            scoresByRegistrant,
            videoPenaltyConfigsByEvent
        ),
        videoPenaltyConfigsByEvent,
    };
};

export const createAwardSyncPlan = (registrants, scoringByRegistrant) => {
    const updates = [];
    let unmatchedRuleCount = 0;

    (registrants || []).forEach((registrant) => {
        const scoring = scoringByRegistrant[registrant.id];
        if (!scoring?.hasScores) return;

        if (scoring.videoPenaltyRuleStatus !== 'matched') unmatchedRuleCount++;
        if (!isAwardSyncNeeded(registrant, scoring)) return;

        updates.push({
            ref: registrant.ref,
            data: {
                averageScore: scoring.finalAverageScore,
                finalAward: scoring.finalAward,
                videoPenaltyConfigRevision: scoring.videoPenaltyConfigRevision,
            },
        });
    });

    return { updates, unmatchedRuleCount };
};

export const syncRegistrantAwards = async () => {
    const registrantsSnapshot = await getDocs(collection(db, 'Registrants2025'));
    const registrants = registrantsSnapshot.docs.map((registrantDocument) => ({
        id: registrantDocument.id,
        ...registrantDocument.data(),
        ref: registrantDocument.ref,
    }));
    const { scoringByRegistrant } = await fetchRegistrantScoringResults(registrants);
    const plan = createAwardSyncPlan(registrants, scoringByRegistrant);

    for (const updateChunk of splitFirestoreInValues(plan.updates, WRITE_BATCH_LIMIT)) {
        const batch = writeBatch(db);
        updateChunk.forEach((update) => batch.update(update.ref, update.data));
        await batch.commit();
    }

    return {
        updatedCount: plan.updates.length,
        unmatchedRuleCount: plan.unmatchedRuleCount,
    };
};
